export const START_YEAR_VULNERABILITY_MSRC = 2016;
export const START_YEAR_VULNERABILITY_REDHAT = 2010;
export const START_YEAR_DETECTED_NODE = 2020;

export const TAG_MICROSOFT = "0";
export const TAG_REDHAT = "1";
export const TAG_APPLE = "2";

export const STATUS_CRITICAL = "2";
export const STATUS_IMPORTANT = "1";
export const STATUS_ATTENTIONAL = "0";

export const SEVERITY_CRITICAL = "2";
export const SEVERITY_IMPORTANT = "1";
export const SEVERITY_ATTENTIONAL = "0";

export const MAX_TITLE_REDHAT = 50;

export const enableTranslation = (page: string) => {
  const enabled: string[] = ["all"];

  return enabled.indexOf("all") >= 0 || enabled.indexOf(page) >= 0;
};

// Translation Result
export type TranslationResult = {
  text: string;
  translatedText: string;
};

export type CVETranslationResult = {
  cveId: string;
  result: TranslationResult;
};

// Request(local)
export type SearchCriteriaItem = {
  freeWord: string;
  termFrom: string;
  termTo: string;
  critical: boolean;
  important: boolean;
  attentional: boolean;
  microsoft: boolean;
  redhat: boolean;
  apple: boolean;
};

// Request
export type RequestPagination = {
  offset: number;
  limit: number;
};

export type ExclusionCVE = {
  cve_id: string;
  excluded: boolean;
};

export type ExclusionNode = {
  managed_node_id: string;
  excluded: boolean;
};

// Response(local)
export type CVEDocument = {
  cve_id: string;
  release_timestamp: string;
  update_timestamp: string;
  description: string;
  maximum_severity_rating: string;
  vulnerability_impact: string;
  affected_software: string[];
};

export type CVRFDocument = {
  title: string;
  release_timestamp: string;
  update_timestamp: string;
  description: string;
  disclaimer: string;
  vulnerability: CVEDocument[];
};

export type MSRCDocument = {
  status: string;
  number_of_nodes: number;
  cvrf: CVRFDocument;
};

export type RedHatAffected = {
  cpe: string;
};

export type RedHatDocument = {
  cve_id: string;
  release_date: string;
  status: string;
  number_of_nodes: number;
  description: string;
  severity: string;
  risk: string;
  affected: RedHatAffected[];
};

// Response
export type ResponsePagination = {
  offset: number;
  limit: number;
  total: number;
};

export type VulnerabilityItem = {
  title: string;
  release_timestamp: number;
  status: string;
  source: string;
  number_of_nodes: number;
  cve_id: string;
  excluded: boolean;
};

export type VulnerabilityMSRC = {
  cve_id: string;
  text: string;
  status: string;
  number_of_nodes: number;
};

export type ManagedNodeItem = {
  managed_node_id: string;
  name: string;
  detect_timestamp: number;
  group_name: string;
  severity: string;
  network_id: string;
  board_id: string;
  excluded: boolean;
};

export type DetectedVulnerabilityItem = {
  cve_id: string;
  title: string;
  release_timestamp: number;
  kb_id: string;
  status: string;
};

export type DetectedManagedNodeItem = {
  managed_node_id: string;
  name: string;
  detect_timestamp: number;
  group_name: string;
  severity: string;
  source: string;
  network_id: string;
  board_id: string;
  detect_vulnerability: {
    title: string;
    cve_id: string;
  };
  vulnerabilities: DetectedVulnerabilityItem[];
};

export type ErrorInfo = {
  code: string;
  message: string;
};

export type Matching = {
  status: string;
  start_timestamp: number;
  end_timestamp: number;
};

// Request Message
export type ExclusionCustomerRequest = {
  exclusions: ExclusionCVE[];
};

export type ExclusionManagedNodeRequest = {
  exclusion: {
    cve_id: string;
    managed_nodes: ExclusionNode[];
  };
};

// Response Message
export type TopResponse = {
  latest: VulnerabilityItem[];
};

export type VulnerabilityResponse = {
  pagination: ResponsePagination;
  vulnerabilities: VulnerabilityItem[];
};

export type VulnerabilityMSRCResponse = {
  vulnerability: VulnerabilityMSRC;
};

export type VulnerabilityManagedNodeResponse = {
  pagination: ResponsePagination;
  managed_nodes: ManagedNodeItem[];
};

export type DetectedManagedNodeResponse = {
  pagination: ResponsePagination;
  managed_nodes: DetectedManagedNodeItem[];
};

export type ExclusionCustomerResponse = {};

export type ExclusionManagedNodeResponse = {};

export type GetTaskMatchingResponse = {
  matching: Matching;
};

export type DeleteTaskMatchingResponse = {};

export type VulnerabilityRedHatResponse = {
  vulnerability: RedHatDocument;
};

// Response Message(Translation API)
export type TranslationResponse = {
  data: {
    translations: {
      translatedText: string;
    }[];
  };
};

const regexAnchor = /<a /gi;
const anchorBlank = '<a target="_blank" ';

// Functions
export const createMSRCDocument = (resp: VulnerabilityMSRCResponse) => {
  const vuln = resp.vulnerability;
  return {
    status: vuln.status,
    number_of_nodes: vuln.number_of_nodes,
    cvrf: createCVRFDocument(vuln),
  };
};

const createCVRFDocument = (vuln: VulnerabilityMSRC) => {
  //  const json = JSON.parse(vuln.text);
  //  const json = JSON.parse(atob(vuln.text));
  // 文字化け対策（https://developer.mozilla.org/ja/docs/Web/API/WindowBase64/btoa#Unicode_Strings）
  const json = JSON.parse(decodeURIComponent(escape(atob(vuln.text))));

  let title = "";
  if (json.DocumentTitle != null && json.DocumentTitle.Value != null) {
    title = json.DocumentTitle.Value;
  }

  let releaseTimestamp = "";
  let updateTimestamp = "";
  if (json.DocumentTracking != null) {
    if (json.DocumentTracking.InitialReleaseDate != null) {
      releaseTimestamp = json.DocumentTracking.InitialReleaseDate;
      updateTimestamp = json.DocumentTracking.InitialReleaseDate;
    }
    if (json.DocumentTracking.CurrentReleaseDate != null) {
      if (releaseTimestamp == "") {
        releaseTimestamp = json.DocumentTracking.CurrentReleaseDate;
      }
      updateTimestamp = json.DocumentTracking.CurrentReleaseDate;
    }
  }

  let description = "";
  let disclaimer = "";
  const documentNotes = json.DocumentNotes;
  if (Array.isArray(documentNotes)) {
    documentNotes.forEach((note) => {
      if (note.Title == "Release Notes" && note.Value != null) {
        description = note.Value;
      }
      if (note.Title == "Legal Disclaimer") {
        disclaimer = note.Value;
      }
    });
  }

  description = description.replace(regexAnchor, anchorBlank);
  disclaimer = disclaimer.replace(regexAnchor, anchorBlank);
  return {
    title: title,
    release_timestamp: releaseTimestamp,
    update_timestamp: updateTimestamp,
    description: description,
    disclaimer: disclaimer,
    vulnerability: createCVEDocuments(json, vuln.cve_id),
  };
};

const createCVEDocuments = (json: any, targetCveId: string) => {
  const documents: CVEDocument[] = [];

  if (
    json.ProductTree == null ||
    !Array.isArray(json.ProductTree.FullProductName)
  ) {
    return documents;
  }
  if (!Array.isArray(json.Vulnerability)) {
    return documents;
  }
  const fullProductName = json.ProductTree.FullProductName;

  json.Vulnerability.forEach((vuln: any) => {
    let cveId = "";
    if (vuln.CVE != null) {
      cveId = vuln.CVE;
    }
    if (cveId !== "" && cveId === targetCveId) {
      // 公開日と最終更新日
      let releaseTimestamp = "";
      let updateTimestamp = "";
      if (
        Array.isArray(vuln.RevisionHistory) &&
        vuln.RevisionHistory.length > 0
      ) {
        vuln.RevisionHistory.forEach((revision: any) => {
          const date = revision.Date;
          if (date != null) {
            if (releaseTimestamp == "" || releaseTimestamp > date) {
              releaseTimestamp = date;
            }
            if (updateTimestamp == "" || updateTimestamp < date) {
              updateTimestamp = date;
            }
          }
        });
      }
      // description
      let description = "";
      if (Array.isArray(vuln.Notes)) {
        vuln.Notes.forEach((note: any) => {
          if (note.Title == "Description" && note.Value != null) {
            description = note.Value;
          }
        });
      }
      // descriptionが入っていない場合、titleが入っていれば代わりにセット
      if (description == "" && vuln.Title != null && vuln.Title.Value != null) {
        description = vuln.Title.Value;
      }
      description = description.replace(regexAnchor, anchorBlank);
      // severityとimpact
      let severity = "None";
      let impact = "None";
      if (Array.isArray(vuln.Threats)) {
        vuln.Threats.forEach((threat: any) => {
          if (
            threat.Type == 3 &&
            threat.Description != null &&
            threat.Description.Value != null
          ) {
            severity = updateSeverity(severity, threat.Description.Value);
          }
          if (
            threat.Type == 0 &&
            threat.Description != null &&
            threat.Description.Value != null
          ) {
            impact = updateImpact(impact, threat.Description.Value);
          }
        });
      }
      // 対象プロダクト
      let products: string[] = [];
      if (Array.isArray(vuln.ProductStatuses)) {
        vuln.ProductStatuses.forEach((status: any) => {
          if (Array.isArray(status.ProductID)) {
            status.ProductID.forEach((productId: string) => {
              const found = fullProductName.find(
                (p: any) => p.ProductID == productId,
              );
              if (found != null && found.Value != null) {
                const reKB = /^\d+$/;
                const kbs: string[] = [];
                vuln.Remediations.forEach((remediation: any) => {
                  if (
                    remediation.Type == 2 &&
                    remediation.Description != null &&
                    remediation.Description.Value != null &&
                    reKB.test(remediation.Description.Value) &&
                    Array.isArray(remediation.ProductID) &&
                    remediation.ProductID.includes(productId)
                  ) {
                    kbs.push("KB" + remediation.Description.Value);
                  }
                });
                if (kbs.length > 0) {
                  products.push(found.Value + "(" + kbs.sort().join(",") + ")");
                } else {
                  products.push(found.Value);
                }
              }
            });
          }
        });
        products = products.sort();
      }
      // documentsに登録
      documents.push({
        cve_id: cveId,
        release_timestamp: releaseTimestamp,
        update_timestamp: updateTimestamp,
        description: description,
        maximum_severity_rating: severity,
        vulnerability_impact: impact,
        affected_software: products,
      });
    }
  });

  return documents;
};

const updateSeverity = (curSeverity: string, newSeverity: string) => {
  const severities = ["Critical", "Important", "Moderate", "Low"];
  for (let i = 0; i < severities.length; i++) {
    if (severities[i] == curSeverity || severities[i] == newSeverity) {
      return severities[i];
    }
  }
  return newSeverity;
};

const updateImpact = (curImpact: string, newImpact: string) => {
  const impacts = [
    "Remote Code Execution",
    "Elevation of Privilege",
    "Denial of Service",
    "Security Feature Bypass",
    "Information Disclosure",
    "Repudiation",
    "Tampering",
    "Spoofing",
    "Defense in Depth",
    "Not a Vulnerability",
  ];
  for (let i = 0; i < impacts.length; i++) {
    if (impacts[i] == curImpact || impacts[i] == newImpact) {
      return impacts[i];
    }
  }
  return newImpact;
};

export const convertStatus = (status: string) => {
  switch (status) {
    case STATUS_IMPORTANT:
      return { label: "重要", color: "orange" };
    case STATUS_CRITICAL:
      return { label: "緊急", color: "red" };
    default:
      return { label: "注意", color: "blue" };
  }
};

export const convertSeverity = (severity: string) => {
  switch (severity) {
    case SEVERITY_IMPORTANT:
      return { label: "重要", color: "orange" };
    case SEVERITY_CRITICAL:
      return { label: "緊急", color: "red" };
    default:
      return { label: "注意", color: "blue" };
  }
};

export const convertSource = (source: string) => {
  switch (source) {
    case TAG_MICROSOFT:
      return "Microsoft";
    case TAG_REDHAT:
      return "Red Hat";
    case TAG_APPLE:
      return "Apple";
    default:
      return "";
  }
};

export const defaultSearchCriteria = () => {
  const today = new Date();
  const toYear = today.getFullYear();
  const toMonth = today.getMonth() + 1;
  const fromYear = toMonth == 1 ? toYear - 1 : toYear;
  const fromMonth = toMonth == 1 ? 12 : toMonth - 1;
  return {
    freeWord: "",
    termFrom: String(fromYear) + "-" + ("0" + fromMonth).slice(-2),
    termTo: String(toYear) + "-" + ("0" + toMonth).slice(-2),
    critical: false,
    important: false,
    attentional: false,
    microsoft: false,
    redhat: false,
    apple: false,
  };
};

export const emptySearchCriteria = () => {
  return {
    freeWord: "",
    termFrom: "",
    termTo: "",
    critical: false,
    important: false,
    attentional: false,
    microsoft: false,
    redhat: false,
    apple: false,
  };
};

export const assignSearchCriteria = (
  target: SearchCriteriaItem,
  source: SearchCriteriaItem,
) => {
  Object.assign(target, {
    freeWord: source.freeWord,
    termFrom: source.termFrom,
    termTo: source.termTo,
    critical: source.critical,
    important: source.important,
    attentional: source.attentional,
    microsoft: source.microsoft,
    redhat: source.redhat,
    apple: source.apple,
  });
};

export const duplicateSearchCriteria = (source: SearchCriteriaItem) => {
  return {
    freeWord: source.freeWord,
    termFrom: source.termFrom,
    termTo: source.termTo,
    critical: source.critical,
    important: source.important,
    attentional: source.attentional,
    microsoft: source.microsoft,
    redhat: source.redhat,
    apple: source.apple,
  };
};

export const searchConditions = (criteria: SearchCriteriaItem) => {
  let termFrom = "";
  if (criteria.termFrom.length > 0) {
    const split = criteria.termFrom.split("-");
    if (split.length == 2) {
      termFrom = split[0] + "年" + split[1] + "月から";
    }
  }
  let termTo = "";
  if (criteria.termTo.length > 0) {
    const split = criteria.termTo.split("-");
    if (split.length == 2) {
      termTo = split[0] + "年" + split[1] + "月まで";
    }
  }
  const term = termFrom + termTo;

  const status: String[] = [];
  if (criteria.critical) {
    status.push(convertStatus(STATUS_CRITICAL).label);
  }
  if (criteria.important) {
    status.push(convertStatus(STATUS_IMPORTANT).label);
  }
  if (criteria.attentional) {
    status.push(convertStatus(STATUS_ATTENTIONAL).label);
  }

  const tag: String[] = [];
  if (criteria.microsoft) {
    tag.push(convertSource(TAG_MICROSOFT));
  }
  if (criteria.redhat) {
    tag.push(convertSource(TAG_REDHAT));
  }
  if (criteria.apple) {
    tag.push(convertSource(TAG_APPLE));
  }

  const conditions: String[] = [];
  if (term.length > 0) {
    conditions.push(term);
  }
  if (status.length > 0) {
    conditions.push("ステータス:" + status.join(","));
  }
  if (tag.length > 0) {
    conditions.push("タグ:" + tag.join(","));
  }

  if (conditions.length > 0) {
    conditions.splice(0, 0, "-");
  }

  return conditions.join(" ");
};

export const translateCVSS = (cvss: string) => {
  const split = cvss.split(":");
  if (split.length == 2) {
    const type = cvssList.find((type) => type.type == split[0]);
    if (type != null) {
      const value = type.values.find((value) => value.value == split[1]);
      if (value != null) {
        return {
          name: split[0] + "(" + type.name + ")",
          value: split[1] + "(" + value.name + ")",
        };
      } else {
        return {
          name: split[0] + "(" + type.name + ")",
          value: split[1],
        };
      }
    }
    return { name: split[0], value: split[1] };
  }
  return null;
};

// CVSSのマッピング情報
const cvssList = [
  {
    type: "AV",
    name: "攻撃元区分",
    values: [
      { value: "N", name: "ネットワーク" },
      { value: "A", name: "隣接" },
      { value: "L", name: "ローカル" },
      { value: "P", name: "物理" },
    ],
  },
  {
    type: "AC",
    name: "攻撃条件の複雑さ",
    values: [
      { value: "L", name: "低" },
      { value: "M", name: "中" },
      { value: "H", name: "高" },
    ],
  },
  {
    type: "PR",
    name: "必要な特権レベル",
    values: [
      { value: "N", name: "不要" },
      { value: "L", name: "低" },
      { value: "H", name: "高" },
    ],
  },
  {
    type: "UI",
    name: "ユーザ関与レベル",
    values: [
      { value: "N", name: "不要" },
      { value: "R", name: "要" },
    ],
  },
  {
    type: "S",
    name: "スコープ",
    values: [
      { value: "U", name: "変更なし" },
      { value: "C", name: "変更あり" },
    ],
  },
  {
    type: "C",
    name: "機密性への影響",
    values: [
      { value: "H", name: "高" },
      { value: "L", name: "低" },
      { value: "N", name: "なし" },
      { value: "C", name: "全面的" },
      { value: "P", name: "部分的" },
    ],
  },
  {
    type: "I",
    name: "完全性への影響",
    values: [
      { value: "H", name: "高" },
      { value: "L", name: "低" },
      { value: "N", name: "なし" },
      { value: "C", name: "全面的" },
      { value: "P", name: "部分的" },
    ],
  },
  {
    type: "A",
    name: "可用性への影響",
    values: [
      { value: "H", name: "高" },
      { value: "L", name: "低" },
      { value: "N", name: "なし" },
      { value: "C", name: "全面的" },
      { value: "P", name: "部分的" },
    ],
  },
  {
    type: "E",
    name: "攻撃される可能性",
    values: [
      { value: "X", name: "未評価" },
      { value: "H", name: "容易に攻撃可能" },
      { value: "F", name: "攻撃可能" },
      { value: "P", name: "実証可能" },
      { value: "U", name: "未実証" },
      { value: "ND", name: "未評価" },
      { value: "POC", name: "実証可能" },
    ],
  },
  {
    type: "RL",
    name: "利用可能な対策のレベル",
    values: [
      { value: "X", name: "未評価" },
      { value: "U", name: "なし" },
      { value: "W", name: "非公式" },
      { value: "T", name: "暫定" },
      { value: "O", name: "正式" },
      { value: "ND", name: "未評価" },
      { value: "TF", name: "非公式" },
      { value: "TF", name: "暫定" },
      { value: "OF", name: "正式" },
    ],
  },
  {
    type: "RC",
    name: "脆弱性情報の信頼性",
    values: [
      { value: "X", name: "未評価" },
      { value: "C", name: "確認済" },
      { value: "R", name: "未確証" },
      { value: "U", name: "未確認" },
      { value: "ND", name: "未評価" },
      { value: "UR", name: "未確証" },
      { value: "UC", name: "未確認" },
    ],
  },
  {
    type: "CR",
    name: "機密性の要求度",
    values: [
      { value: "X", name: "未評価" },
      { value: "H", name: "高" },
      { value: "M", name: "中" },
      { value: "L", name: "低" },
      { value: "ND", name: "未評価" },
    ],
  },
  {
    type: "IR",
    name: "完全性の要求度",
    values: [
      { value: "X", name: "未評価" },
      { value: "H", name: "高" },
      { value: "M", name: "中" },
      { value: "L", name: "低" },
      { value: "ND", name: "未評価" },
    ],
  },
  {
    type: "AR",
    name: "機密性の要求度",
    values: [
      { value: "X", name: "未評価" },
      { value: "H", name: "高" },
      { value: "M", name: "中" },
      { value: "L", name: "低" },
      { value: "ND", name: "未評価" },
    ],
  },
  {
    type: "MAV",
    name: "緩和策後の攻撃元区分",
    values: [
      { value: "X", name: "未評価" },
      { value: "N", name: "ネットワーク" },
      { value: "A", name: "隣接" },
      { value: "L", name: "ローカル" },
      { value: "P", name: "物理" },
    ],
  },
  {
    type: "MAC",
    name: "緩和策後の攻撃条件の複雑さ",
    values: [
      { value: "X", name: "未評価" },
      { value: "L", name: "低" },
      { value: "H", name: "高" },
    ],
  },
  {
    type: "MPR",
    name: "緩和策後の必要な特権レベル",
    values: [
      { value: "X", name: "未評価" },
      { value: "N", name: "不要" },
      { value: "L", name: "低" },
      { value: "H", name: "高" },
    ],
  },
  {
    type: "MUI",
    name: "緩和策後のユーザ関与レベル",
    values: [
      { value: "X", name: "未評価" },
      { value: "N", name: "不要" },
      { value: "R", name: "要" },
    ],
  },
  {
    type: "MS",
    name: "緩和策後のスコープ",
    values: [
      { value: "X", name: "未評価" },
      { value: "U", name: "変更なし" },
      { value: "C", name: "変更あり" },
    ],
  },
  {
    type: "MC",
    name: "緩和策後の機密性への影響",
    values: [
      { value: "X", name: "未評価" },
      { value: "H", name: "高" },
      { value: "L", name: "低" },
      { value: "N", name: "なし" },
    ],
  },
  {
    type: "MI",
    name: "緩和策後の完全性への影響",
    values: [
      { value: "X", name: "未評価" },
      { value: "H", name: "高" },
      { value: "L", name: "低" },
      { value: "N", name: "なし" },
    ],
  },
  {
    type: "MA",
    name: "緩和策後の可用性への影響",
    values: [
      { value: "X", name: "未評価" },
      { value: "H", name: "高" },
      { value: "L", name: "低" },
      { value: "N", name: "なし" },
    ],
  },
  {
    type: "Au",
    name: "攻撃前認証要否",
    values: [
      { value: "?", name: "複数" },
      { value: "?", name: "単一" },
      { value: "?", name: "不要" },
    ],
  },
  {
    type: "CDP",
    name: "二次的被害",
    values: [
      { value: "?", name: "なし" },
      { value: "?", name: "軽微" },
      { value: "?", name: "中程度" },
      { value: "?", name: "重大" },
      { value: "?", name: "壊滅的" },
      { value: "?", name: "未評価" },
    ],
  },
  {
    type: "TD",
    name: "システム範囲",
    values: [
      { value: "?", name: "なし" },
      { value: "?", name: "小規模" },
      { value: "?", name: "中規模" },
      { value: "?", name: "大規模" },
      { value: "?", name: "未評価" },
    ],
  },
];
